import { useParams } from "react-router";
import useHeaderChange from "@hooks/useHeaderChange";
import { type Gift, useGifts } from "@hooks/useGifts";
import DataGrid, { Column, DataGridRef } from "devextreme-react/data-grid";
import { formatNumber } from "devextreme/localization";
import GiftingLoading from "./GiftingSkeleton";
import { cellRenderer, headerRenderer } from "./grid-utils";
import { useEffect, useMemo, useRef, useState } from "react";
import NoDataMessage from "@/components/NoDataMessage";
import withDisclaimer from "@/hoc/withDisclaimer";
import { onContentReadySortArrow } from "@/helper/DxHelper";

const columnWidth = 200;

function Gifting() {
  const params = useParams();
  const { data, isLoading, isError, isSuccess: giftsFetched, isRefetching } = useGifts(params.id!);
  const [gridWidth, setGridWidth] = useState(0);
  useHeaderChange(params.id!, "Gifting");
  const dxGridRef = useRef<DataGridRef>(null);

  const totalGiftedClients = useMemo(
    () => Array.from(new Set(data?.data?.gifts?.map((gift) => gift.recipientId))),
    [giftsFetched]
  );

  useEffect(() => {
    setGridWidth(columnWidth * (totalGiftedClients.length + 1));
  }, [totalGiftedClients]);

  const mergedData = useMemo(
    () =>
      data?.data.gifts?.reduce((result: Gift[], item) => {
        const { year, recipientId, amount, comments } = item;
        const existingItem = result.find((gift) => gift.year === year);

        if (existingItem) {
          existingItem?.recipientIds?.push(recipientId);
          existingItem?.amounts?.push(amount);
          existingItem?.commentsArr?.push(comments);
        } else {
          result.push({
            ...item,
            recipientIds: [recipientId],
            amounts: [amount],
            commentsArr: [comments],
          });
        }

        return result;
      }, []),
    [giftsFetched]
  );
  if (isLoading || isRefetching) return <GiftingLoading />;
  if (isError) return <div>Failed fetching Gifts</div>;

  if (!data?.data?.gifts?.length) return <NoDataMessage />;

  return (
    <div>
      <DataGrid
        className="ao-grid"
        dataSource={mergedData ?? []}
        showBorders={true}
        showRowLines={true}
        showColumnLines={true}
        ref={dxGridRef}
        onContentReady={() => onContentReadySortArrow(dxGridRef)}
        wordWrapEnabled={true}
        keyExpr={"externalCode"}
        onCellPrepared={(e) => {
          if (e.rowType === "header") {
            const style = e.cellElement.style;
            style.backgroundColor = "var(--primary-color)";
            style.color = "white";
            style.borderBottom = "1px solid #fafafa";
          }
        }}
      >
        <Column
          dataField="year"
          allowSorting={false}
          sortOrder={"asc"}
          allowEditing={false}
          width={columnWidth}
          minWidth={columnWidth}
          alignment={"center"}
          headerCellRender={() => headerRenderer(`year`, "Year of gifting")}
          cellRender={(cellData) => {
            return cellData.data.amount
              ? cellRenderer(
                  cellData,
                  "amount1",
                  cellData?.data.year,
                  "",
                  "bg-primary text-white font-medium"
                )
              : "";
          }}
        />

        {totalGiftedClients.map((recipientId, i) => (
          <Column
            dataField={`amount-${i}`}
            allowSorting={false}
            key={recipientId}
            allowEditing={false}
            width={columnWidth}
            minWidth={columnWidth}
            alignment={"center"}
            headerCellRender={() =>
              headerRenderer(
                `amount-${i}`,
                data?.data?.contacts?.find(({ externalCode }) => recipientId === externalCode)
                  ?.preferredFirstName ||
                  data?.data?.contacts?.find(({ externalCode }) => recipientId === externalCode)
                    ?.preferredFullName
              )
            }
            cellRender={(cellData) => {
              if (cellData.data.recipientIds.length < 2) {
                const idx = cellData.data.recipientIds.findIndex((w: string) => w === recipientId);

                return cellData.data
                  ? cellRenderer(
                      cellData,
                      `amount-${i}`,
                      formatNumber(cellData?.data.amounts[idx], "currency"),
                      cellData.data.commentsArr[idx]
                    )
                  : "";
              }
              return cellData.data
                ? cellRenderer(
                    cellData,
                    `amount-${i}`,
                    formatNumber(cellData?.data.amounts[i], "currency"),
                    cellData.data.commentsArr[i]
                  )
                : "";
            }}
          />
        ))}
      </DataGrid>

      <div
        style={{ width: gridWidth }}
        className="relative -top-[1px] left-[0.75px] flex text-sm text-neutral-100 print:-top-2 print:py-1 print:!text-[10px]"
      >
        <div
          style={{
            WebkitPrintColorAdjust: "exact",
            width: columnWidth,
          }}
          className="flex-1 bg-primary py-[13px] text-center font-medium text-white print:py-[9px] print:!text-[10px]"
        >
          TOTAL
        </div>
        {totalGiftedClients.map((id) => {
          const totalAmountForClient = data?.data?.gifts
            .filter(({ recipientId }) => recipientId === id)
            .reduce((prev, curr) => prev + curr.amount, 0);

          return (
            <div
              style={{
                width: columnWidth,
                maxWidth: columnWidth,
              }}
              className="flex-1 border border-r-0 border-neutral-400 py-[13px] text-center last:border-r print:py-[9px] print:!text-[10px]"
              key={id}
            >
              {formatNumber(totalAmountForClient, "currency")}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default withDisclaimer(Gifting);
